import { useEffect, useRef } from "react";

/**
 * Safely renders an HTML embed snippet (iframes, divs, widgets, scripts).
 *
 * Why this component exists:
 * React's `dangerouslySetInnerHTML` inserts HTML into the DOM but the browser
 * does NOT execute any `<script>` tags found inside it. Most third-party
 * widgets (LeadConnector / HighLevel review widget, Google Reviews, Elfsight,
 * Calendly, etc.) need their script to run — for auto-resize, init, posting
 * messages to the iframe, etc. Without it, the iframe shell renders but the
 * widget is empty or wrong-height.
 *
 * What this does:
 * 1. Renders the embed HTML into a host div.
 * 2. Walks every <script> tag in the snippet, creates a fresh <script>
 *    element with the same src / inline content / attributes, and appends
 *    it to the container so the browser actually executes it.
 * 3. Cleans up scripts and inline iframes on unmount so navigating between
 *    listings doesn't leak globals or stack widgets.
 *
 * Security:
 * Only listing owners can paste embed codes (gated by dashboard auth + their
 * own listing). We trust their content (same security level as them editing
 * their own About / services text). The viewer of the listing trusts the
 * listing owner the same way they would with any embedded review widget.
 */
export default function EmbedRenderer({ html, className = "" }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !html) return;

    // Inject the snippet's HTML.
    host.innerHTML = html;

    // Find every <script> tag the user pasted in and re-create it so the
    // browser actually runs it.
    const scripts = Array.from(host.querySelectorAll("script"));
    const appended = [];
    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script");
      // Copy attributes (src, type, async, defer, etc.)
      for (const { name, value } of Array.from(oldScript.attributes)) {
        newScript.setAttribute(name, value);
      }
      // Copy inline JS body (in case it's not an external src)
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      // Replace the inert tag with the executable one (preserves DOM order
      // so any iframes that come after the script still get auto-resized).
      oldScript.parentNode.replaceChild(newScript, oldScript);
      appended.push(newScript);
    });

    return () => {
      // Cleanup: detach the scripts we added so they don't double-init when
      // React unmounts/remounts (e.g. soft route changes).
      appended.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
      if (host) host.innerHTML = "";
    };
  }, [html]);

  if (!html) return null;
  return <div ref={hostRef} className={`widget-embed ${className}`} />;
}
